import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import { createAsyncFilterLabelCache } from '#/modules/commonComponents/table/asyncFilterLabelCache';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { coerceFilterValues } from '#/modules/commonComponents/table/columnFilterUtils';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import {
  hydrateCustomerFilterLabels,
} from '#/modules/crmMgmt/customer/helper/searchCustomerFilterOptions';
import ProductSideMsg from '#/modules/crmMgmt/product/components/ProductSideMsg';
import { buildProductColumns } from '#/modules/crmMgmt/product/data/productColumns';
import { getProductApiErrorMessage } from '#/modules/crmMgmt/product/helper/apiError';
import useProductList from '#/modules/crmMgmt/product/hooks/useProductList';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';

const PRODUCT_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  productNo: '產品代號',
  status: '狀態',
  customerProductNo: '客戶料號',
  salesUnitName: '銷售單位',
  remark: '備註',
};

const ProductListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.PRODUCT_CREATE);
  const canUpdate = useCan(PERM.PRODUCT_UPDATE);

  const [customerLabelCache] = useState(createAsyncFilterLabelCache);
  const [labelCacheVersion, setLabelCacheVersion] = useState(0);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    removeSortAt,
    resetListQuery,
  } = useProductList();

  useEffect(() => {
    let cancelled = false;
    const rowCustomerIds = rows.map((row) => row.customerId).filter(Boolean);
    const ids = [
      ...coerceFilterValues(listQuery.filters?.customerId),
      ...rowCustomerIds,
    ];

    hydrateCustomerFilterLabels(ids, customerLabelCache)
      .then((didHydrate) => {
        if (!cancelled && didHydrate) {
          setLabelCacheVersion((version) => version + 1);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    listQuery.filters?.customerId,
    rows,
    customerLabelCache,
  ]);

  const columns = useMemo(
    () => {
      void labelCacheVersion;
      return buildProductColumns({
        filters: listQuery.filters || {},
        onFilterChange: setFilter,
        customerLabelCache,
        canShowEdit: () => canUpdate,
      });
    },
    [listQuery.filters, setFilter, customerLabelCache, labelCacheVersion, canUpdate],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.productCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">產品管理</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="crm.product.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getProductApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋代號、名稱、客戶料號或備註"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={PRODUCT_SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
            />
          </div>

          <ProductSideMsg
            displayData={displayData}
            setSelectedData={setSelectedData}
            customerLabelCache={customerLabelCache}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default ProductListPage;
